import { forwardRef, PropsWithoutRef } from 'react';
import { createSafeContext } from '@mews-framework/utils';
import { DateTimeUtc } from '../dateTimesUtc';
import { AppClockService } from './appClockService';

const {
    AppClockServiceContextConsumer: Consumer,
    AppClockServiceContextProvider: Provider,
    useAppClockServiceContext: useAppClockService,
} = createSafeContext<'AppClockServiceContext', AppClockService>('AppClockServiceContext');

export interface AppClockServiceProps {
    appClockService: AppClockService;
}

export const createAppClockServiceProvider =
    <P,>(appClockService: AppClockService) =>
    (props: P) => <Provider value={appClockService} {...props} />;

export const withAppClockService = <P,>(Component: React.ComponentType<P & AppClockServiceProps>) =>
    forwardRef((props: PropsWithoutRef<P>, ref) => (
        <Consumer>
            {(appClockService) => <Component {...(props as P)} appClockService={appClockService} ref={ref} />}
        </Consumer>
    ));

export { useAppClockService };

interface AppClockValueProps {
    appClockValue: number;
}

export const withAppClockValue = <P,>(Component: React.ComponentType<P & AppClockValueProps>) =>
    forwardRef((props: PropsWithoutRef<P>, ref) => (
        <Consumer>
            {(appClockService) => <Component {...(props as P)} appClockValue={appClockService.getValue()} ref={ref} />}
        </Consumer>
    ));

export const useAppClockValue = () => useAppClockService().getValue();

interface AppNowUtcProps {
    appNowUtc: DateTimeUtc;
}

export const withAppNowUtc = <P,>(Component: React.ComponentType<P & AppNowUtcProps>) =>
    forwardRef((props: PropsWithoutRef<P>, ref) => (
        <Consumer>
            {(appClockService) => <Component {...(props as P)} appNowUtc={appClockService.getAppNowUtc()} ref={ref} />}
        </Consumer>
    ));

export const useAppNowUtc = () => useAppClockService().getAppNowUtc();
